import { Network } from "@capacitor/network";
import { isNative } from "@/platform";
import { apiClient } from "@/api/client";

export function initNetworkListener(setIsOnline: (v: boolean) => void): () => void {
  if (isNative()) {
    void Network.getStatus().then(({ connected }) => setIsOnline(connected));
    void Network.addListener("networkStatusChange", ({ connected }) => {
      setIsOnline(connected);
    });
    return () => {};
  }

  // Web fallback. The browser's online flag is only a hint (MV2-1258): an
  // `offline` event whose `online` never arrives (Wi-Fi blip, sleep/wake,
  // VPN reconnect) left the banner up while the API kept answering. So any
  // response from our API — whatever its status — means online, and so does
  // the browser saying so when the tab comes back to the front. Only the
  // browser saying offline turns the banner on.
  const online = (): void => setIsOnline(true);
  const offline = (): void => setIsOnline(false);
  const recheck = (): void => {
    if (document.visibilityState === "visible" && navigator.onLine) setIsOnline(true);
  };
  const interceptor = apiClient.interceptors.response.use(
    (response) => {
      setIsOnline(true);
      return response;
    },
    (error: unknown) => {
      if (error && typeof error === "object" && "response" in error && error.response) {
        setIsOnline(true);
      }
      return Promise.reject(error);
    },
  );
  window.addEventListener("online", online);
  window.addEventListener("offline", offline);
  window.addEventListener("focus", recheck);
  document.addEventListener("visibilitychange", recheck);
  setIsOnline(navigator.onLine);

  return () => {
    apiClient.interceptors.response.eject(interceptor);
    window.removeEventListener("online", online);
    window.removeEventListener("offline", offline);
    window.removeEventListener("focus", recheck);
    document.removeEventListener("visibilitychange", recheck);
  };
}
